import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import { companyMilestones, testimonials, faqData } from '@/mocks/sportsData';

const aboutHeroImg = 'https://readdy.ai/api/search-image?query=Modern%20sports%20media%20office%20with%20large%20wall%20screens%20displaying%20live%20sports%20broadcasts%2C%20warm%20coral%20amber%20lighting%2C%20professional%20creative%20workspace%2C%20collaborative%20atmosphere%2C%20clean%20modern%20design%2C%20editorial%20photography%20style%2C%20sports%20broadcast%20center%20aesthetic&width=1800&height=600&seq=about-hero-2026&orientation=landscape';

export default function About() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setContactStatus('success');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    const textarea = formData.get('message') as string;
    if (textarea && textarea.length > 500) {
      setContactStatus('error');
      setContactError('留言内容不能超过500个字符');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9s99u7nahb7382ovsgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || text;
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        setContactStatus('error');
        setContactError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setContactStatus('error');
      setContactError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 每日大赛在线观看 | 品牌介绍与联系方式"
        description="每日大赛在线观看品牌介绍。了解我们的发展历程、核心优势与用户口碑。联系我们获取赛事直播服务支持，电话400-888-9999。"
        keywords="每日大赛在线观看,关于每日大赛,体育直播平台介绍,每日大赛联系方式"
        canonicalUrl="https://www.meiri-dasai.com/about"
      />
      <SchemaOrg type="Organization" data={{ name: '每日大赛在线观看', url: 'https://www.meiri-dasai.com/', description: '汇聚全球顶级赛事直播资源，高清免费在线观看', contactPoint: { '@type': 'ContactPoint', telephone: '400-888-9999', contactType: 'customer service', availableLanguage: 'Chinese' }, address: { '@type': 'PostalAddress', addressLocality: '北京', addressRegion: '朝阳区', streetAddress: '体育产业园A座' } }} />
      <SchemaOrg type="WebPage" data={{ name: '关于我们 - 每日大赛在线观看', url: 'https://www.meiri-dasai.com/about', isPartOf: { '@type': 'WebSite', name: '每日大赛在线观看' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.meiri-dasai.com/' }, { '@type': 'ListItem', position: 2, name: '关于我们' }] }} />
      <SchemaOrg type="FAQPage" data={{ mainEntity: faqData.slice(0, 4).map((faq) => ({ '@type': 'Question', name: faq.question, acceptedAnswer: { '@type': 'Answer', text: faq.answer } })) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-4 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        <main>
          {/* About Hero */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-[960px] mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">关于每日大赛在线观看</h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-[700px] mx-auto leading-relaxed">
                每日大赛在线观看成立于2020年，致力于为全球体育爱好者提供高清、流畅的赛事直播服务。我们汇聚全球顶级赛事资源，覆盖足球、篮球、电竞、网球等多个热门项目，让每一位用户都能随时随地享受精彩赛事。
              </p>
            </div>
          </section>

          {/* Brand Image */}
          <section className="px-4 md:px-6 bg-background-100 pb-10 md:pb-14">
            <div className="max-w-[960px] mx-auto">
              <div className="h-[280px] md:h-[400px] rounded-lg overflow-hidden">
                <img src={aboutHeroImg} alt="每日大赛在线观看 - 品牌展示" className="w-full h-full object-cover" />
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-[960px] mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '7+', label: '年行业经验' },
                  { value: '500万+', label: '注册用户' },
                  { value: '20+', label: '体育项目' },
                  { value: '99.9%', label: '直播稳定性' },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center p-5 bg-background-100 rounded-lg border border-background-200/70">
                    <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</p>
                    <p className="text-xs text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-[960px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300/60 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {companyMilestones.map((m, idx) => (
                    <div key={idx} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="md:w-1/2 flex items-start md:justify-end gap-4 pl-10 md:pl-0">
                        {idx % 2 === 0 ? (
                          <div className="flex-1 text-right hidden md:block">
                            <span className="text-lg font-heading font-bold text-primary-500">{m.year}</span>
                            <h3 className="text-sm font-semibold text-foreground-900 mt-0.5">{m.title}</h3>
                            <p className="text-xs text-foreground-600 mt-1">{m.description}</p>
                          </div>
                        ) : null}
                      </div>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className="md:w-1/2 pl-10 md:pl-8">
                        {idx % 2 !== 0 ? (
                          <div className="flex-1 hidden md:block">
                            <span className="text-lg font-heading font-bold text-primary-500">{m.year}</span>
                            <h3 className="text-sm font-semibold text-foreground-900 mt-0.5">{m.title}</h3>
                            <p className="text-xs text-foreground-600 mt-1">{m.description}</p>
                          </div>
                        ) : (
                          <div className="md:hidden">
                            <span className="text-lg font-heading font-bold text-primary-500">{m.year}</span>
                            <h3 className="text-sm font-semibold text-foreground-900 mt-0.5">{m.title}</h3>
                            <p className="text-xs text-foreground-600 mt-1">{m.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-[960px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-600 text-center mb-8">来自真实用户的使用评价</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary-600">{t.name[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-[960px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">联系我们</h2>
              <p className="text-sm text-foreground-600 text-center mb-8">有任何问题或建议？欢迎随时联系我们</p>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">support@meiri-dasai.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 flex-shrink-0">
                      <i className="ri-phone-line text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">客服热线</h4>
                      <p className="text-sm text-foreground-600">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100/70 flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">公司地址</h4>
                      <p className="text-sm text-foreground-600">北京市朝阳区体育产业园A座</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 flex-shrink-0">
                      <i className="ri-time-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">工作时间</h4>
                      <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  {contactStatus === 'success' ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-green-100">
                        <i className="ri-check-line text-2xl text-green-500"></i>
                      </div>
                      <p className="text-sm font-medium text-green-700">提交成功！感谢您的留言，我们会在24小时内回复您。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleContact} data-readdy-form="" className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input type="text" name="name" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-md outline-none focus:border-primary-400 transition-colors" placeholder="请输入您的姓名" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input type="email" name="email" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-md outline-none focus:border-primary-400 transition-colors" placeholder="请输入您的邮箱" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                        <input type="text" name="subject" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-md outline-none focus:border-primary-400 transition-colors" placeholder="请输入留言主题" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容 <span className="text-foreground-400 font-normal">(最多500字)</span></label>
                        <textarea name="message" required maxLength={500} rows={4} className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-md outline-none focus:border-primary-400 transition-colors resize-none" placeholder="请输入您的留言内容..."></textarea>
                      </div>
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                      <button type="submit" className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                        提交留言
                      </button>
                    </form>
                  )}
                  {contactStatus === 'error' && (
                    <p className="text-sm text-red-500 mt-2">{contactError}</p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}